import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useWorkspaceActivities, type ActivityView, type ActivityWithContact } from "@/hooks/useWorkspaceActivities";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Clock, CalendarCheck, ArrowRight, Phone, Video, Mail, MessageSquare, Users, Calendar } from "lucide-react";

const VIEW_CONFIG: Record<ActivityView, { label: string; icon: typeof Clock; description: string }> = {
  today: { label: "Today", icon: CalendarCheck, description: "Activities scheduled for today" },
  missed: { label: "Missed", icon: Clock, description: "Past activities not yet completed" },
  upcoming: { label: "Today+", icon: ArrowRight, description: "Upcoming activities from tomorrow onwards" },
};

const TYPE_ICONS: Record<string, typeof Phone> = {
  phone_call: Phone,
  video_call: Video,
  email: Mail,
  sms: MessageSquare,
  in_person: Users,
};

export function ActivitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentView = (searchParams.get("view") as ActivityView) || "today";
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const { activities, totalCount, isLoading } = useWorkspaceActivities({
    view: currentView,
    page,
    pageSize,
  });

  const handleViewChange = (view: ActivityView) => {
    setSearchParams({ view });
    setPage(0);
  };

  const handleRowClick = (activity: ActivityWithContact) => {
    navigate(`/contacts/${activity.contact_id}`);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatType = (type: string | null | undefined) => {
    if (!type) return "Activity";
    return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex justify-between items-center p-4">
        <div>
          <h1 className="text-2xl font-bold">Activities</h1>
          <p className="text-sm text-muted-foreground">{VIEW_CONFIG[currentView].description}</p>
        </div>
      </div>

      {/* View tabs */}
      <div className="flex gap-1 px-4 pb-4">
        {(Object.keys(VIEW_CONFIG) as ActivityView[]).map((view) => {
          const config = VIEW_CONFIG[view];
          const Icon = config.icon;
          const isActive = view === currentView;

          return (
            <Button
              key={view}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => handleViewChange(view)}
              className="gap-1.5"
            >
              <Icon className="h-3.5 w-3.5" />
              {config.label}
            </Button>
          );
        })}
        <Badge variant="secondary" className="ml-2 self-center">
          {totalCount} {totalCount === 1 ? "activity" : "activities"}
        </Badge>
      </div>

      {/* Table */}
      <div className="flex-1 px-4 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            Loading activities...
          </div>
        ) : activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <p className="text-lg font-medium">No activities</p>
            <p className="text-sm">
              {currentView === "missed" && "No missed activities. You're all caught up!"}
              {currentView === "today" && "No activities scheduled for today."}
              {currentView === "upcoming" && "No upcoming activities scheduled."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Contact</TableHead>
                <TableHead>Activity</TableHead>
                <TableHead className="w-[100px]">Type</TableHead>
                <TableHead className="w-[180px]">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activities.map((activity) => {
                const TypeIcon = (activity.type && TYPE_ICONS[activity.type]) || Users;
                return (
                  <TableRow
                    key={activity.id}
                    onClick={() => handleRowClick(activity)}
                    className="cursor-pointer"
                  >
                    <TableCell className="font-medium">
                      {[activity.contact_first_name, activity.contact_last_name]
                        .filter(Boolean)
                        .join(" ") || "Unknown"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span>{activity.title}</span>
                        {activity.calendar_event_id && (
                          <span title="On calendar">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <TypeIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs">{formatType(activity.type)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(activity.start_date)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
